/**
 * An uploaded image as it can arrive from core-api: embedded, a bare IRI, or absent. Taken as
 * `unknown` at the call sites because most site/user types carry `image` loosely typed.
 */
export type MediaLike = string | { contentUrl?: string | null } | null | undefined

/**
 * Absolute URL for an uploaded image, ready to drop into an `<img>` — or `undefined` when
 * there is nothing renderable, so callers can fall back to a placeholder icon.
 *
 * Two API quirks are absorbed here:
 *
 * 1. **Shape.** `image` is only expanded into `{ contentUrl }` when the active serialization
 *    group lists `MediaObject.contentUrl`; otherwise API Platform sends a bare IRI string
 *    (`"/media_objects/…"`), which is not a file URL. Binding that straight into an `<img>`
 *    is what produced broken vessel photos in the site picker. A string IRI yields
 *    `undefined` here rather than a dead `src`.
 * 2. **Scheme.** core-api builds `contentUrl` from Vich's `uri_prefix`, which is pinned to
 *    `http://`. On the https deployments the browser blocks that as mixed content, so the
 *    scheme is upgraded to match the page — and only when the page is https, leaving a local
 *    http dev server alone.
 *
 * `?x-request=html` is the platform convention (Fleet Commander appends it to every media URL)
 * for getting the file itself back rather than a JSON-LD representation of it.
 */
export function mediaUrl (image: unknown): string | undefined {
  // A bare IRI reference carries no file URL — the group did not expand the media object.
  const contentUrl = typeof image === 'string' ? undefined : (image as Exclude<MediaLike, string>)?.contentUrl

  if (!contentUrl) return undefined

  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? contentUrl.replace(/^http:\/\//i, 'https://')
      : contentUrl

  return `${secure}${secure.includes('?') ? '&' : '?'}x-request=html`
}
